import { useMemo } from "react";

import { useQueryPatientsQuery } from "services/patients";
import type { Patient } from "services/types/patients.type";

// Dedicated field set for the pop card: identity (incl. the hero's pronouns /
// age / patient-since line), the patient's tags (the practice's own shorthand
// for who this is — "VIP", "Spanish speaking", "Hard of hearing"), their
// preferred provider(s), next-appointment context (time/type/provider), the
// actionable flags (outstanding patient balance, incomplete onboarding
// forms), payment source (declared type + active primary policy's insurer)
// and the per-ear hearing aid devices (wearer vs pending order). NOT the
// shared DEFAULT_PATIENTS_QUERY: several of these are per-patient computed
// props that belong only on this ≤3-patient fetch. RestQL rejects unknown
// query fields, so any field added here must already exist on the deployed
// backend.
//
// `assigned_tags` comes back with expired tags still in it (the backend only
// drops discarded and conversation tags); `activeTags` in the context util
// filters them.
export const INCOMING_CALL_PATIENTS_QUERY =
  "{id,first_name,middle_name,last_name,preferred_name,title,suffix,photo," +
  "pronouns,birthdate,created_at," +
  "assigned_tags{id,description,expires_at,tag{id,name,color,tooltip}}," +
  "preferred_provider{id,first_name,last_name,full_name,title,suffix}," +
  "secondary_preferred_provider{id,first_name,last_name,full_name,title,suffix}," +
  "secondary_preferred_provider_role," +
  "next_appointment{id,type{name},start_moment,clinic{timezone}," +
  "staff_member{first_name,last_name}}," +
  "patient_responsibility_balance,number_of_pending_intake_forms," +
  "payment_source_type_display,primary_insurance_policy{id,insurer{name}}," +
  "left_ear_hearing_aid{id,is_in_use,delivered_at}," +
  "right_ear_hearing_aid{id,is_in_use,delivered_at}}";

// Nearest upcoming appointment first; patients without one last. The pop
// requirement: when several patients share a number, lead with whoever is
// most likely the caller (they have the appointment coming up).
export function sortByUpcomingAppointment(patients: Patient[]) {
  const startOf = (patient: Patient) =>
    patient.next_appointment?.start_moment
      ? new Date(patient.next_appointment.start_moment).getTime()
      : Number.POSITIVE_INFINITY;
  return [...patients].sort((a, b) => startOf(a) - startOf(b));
}

// One request hydrates every backend-matched patient.
export default function useIncomingCallPatients(patientIds: number[]) {
  const { data, isLoading } = useQueryPatientsQuery(
    { ids: patientIds, baseFilter: {}, query: INCOMING_CALL_PATIENTS_QUERY },
    { skip: patientIds.length === 0 }
  );

  const patients = useMemo(() => sortByUpcomingAppointment(data ?? []), [data]);

  return { patients, isLoading };
}
